// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module RealtimeBadge
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS realtime-visitor badge used by
 * dashboards to surface current activity at a glance. The badge handles
 * loading, empty, and active states without requiring consumer branching.
 */

import type { RealtimeStats } from "@geenius/analytics";
import { type Component, Show } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";

/**
 * Props for rendering the SolidJS CSS realtime activity badge.
 *
 * @property stats Latest realtime stats snapshot, or `null` when unavailable.
 * @property isLoading Whether the realtime fetch is still pending.
 * @property class Optional class name merged into the badge wrapper.
 */
export interface RealtimeBadgeProps {
  stats: RealtimeStats | null;
  isLoading: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  class?: string;
}

function getRealtimeErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

/**
 * Renders a SolidJS CSS badge showing the current active-visitor count.
 *
 * @param props Realtime stats and loading state for the badge.
 * @returns A badge element representing loading, empty, or active realtime state.
 */
export const RealtimeBadge: Component<RealtimeBadgeProps> = (props) => {
  const copy = createAnalyticsCopy();
  const activeLabel = () =>
    props.stats
      ? `${props.stats.activeVisitors} ${copy.t("analytics.realtime.activeNow")}`
      : copy.t("analytics.realtime.activeNow");

  return (
    <Show
      when={!props.error}
      fallback={
        <span
          class={cx(
            "gn-analytics-realtime",
            "gn-analytics-realtime--error",
            props.class,
          )}
          data-analytics-error-state
          role="alert"
          aria-live="assertive"
        >
          <span aria-hidden="true">!</span>
          <span>{getRealtimeErrorMessage(props.error ?? "")}</span>
          <Show when={props.onRetry}>
            {(onRetry) => (
              <button
                type="button"
                class="gn-analytics-realtime__retry"
                onClick={onRetry()}
              >
                Retry
              </button>
            )}
          </Show>
        </span>
      }
    >
      <Show
        when={!props.isLoading}
        fallback={
          <span
            class={cx(
              "gui-badge",
              "gui-badge--secondary",
              "gn-analytics-realtime",
              props.class,
            )}
            role="status"
            aria-live="polite"
            aria-label="Loading realtime analytics"
          >
            <span aria-hidden="true">⏳</span>
            <span class="gn-analytics-a11y__sr-only">
              Loading realtime analytics
            </span>
          </span>
        }
      >
        <Show
          when={props.stats}
          fallback={
            <span
              class={cx(
                "gui-badge",
                "gui-badge--secondary",
                "gn-analytics-realtime",
                props.class,
              )}
              role="status"
              aria-live="polite"
              aria-label="Realtime analytics unavailable"
            >
              <span aria-hidden="true">—</span>
              <span class="gn-analytics-a11y__sr-only">
                Realtime analytics unavailable
              </span>
            </span>
          }
        >
          {(stats) => (
            <span
              class={cx(
                "gui-badge",
                stats().activeVisitors > 0
                  ? "gui-badge--success"
                  : "gui-badge--secondary",
                "gn-analytics-realtime",
                props.class,
              )}
              role="status"
              aria-live="polite"
              aria-label={activeLabel()}
            >
              <span aria-hidden="true">🟢</span>
              <span class="gn-analytics-realtime__count">
                {stats().activeVisitors}
              </span>
              <span>{copy.t("analytics.realtime.activeNow")}</span>
            </span>
          )}
        </Show>
      </Show>
    </Show>
  );
};
